import { SentryRoutes } from '@config/instrument';

import { Route } from 'react-router-dom';

import Layout from '@components/Layout/Layout';
import { CheckoutPSTNUpgrade } from '@components/Order/Numbers/CheckoutNumbers/CheckoutNumbersForPlan';
import { ApiPage } from '@containers/Api';
import {
  BillingLayout,
  Balance,
  BalanceHistory,
  Invoices,
  Details as InvoiceDetails,
} from '@containers/Billing';
import {
  CapacityLayout,
  DedicatedChannels,
  Trunks,
} from '@containers/Capacity';
import {
  DidGroupCreate,
  DidGroupsExisting,
  DidGroupDetails,
  DidGroupDelete,
  DidGroupRename,
} from '@containers/Capacity/DidGroups';
import { TrunkDetails } from '@containers/Capacity/Trunks/TrunkDetails';
import { Checkout } from '@containers/Cart';
import { Dashboard } from '@containers/Dashboard';
import { Page404 } from '@containers/ErrorPages';
import { Login, Logout } from '@containers/Login';
import { MyNumbers, NumbersLayout, Details } from '@containers/Numbers';
import {
  OrderLayout,
  OrderSharedChannels,
  History as OrderHistory,
  OrderPSTNUpgrade,
  OpenOrders,
  OrderNumbers,
  DedicatedChannels as OrderDedicatedChannels,
  CheckoutNumbersForPlan,
  SelectNumbers,
  SelectBackorders,
  CancelDedicatedChannels,
  Details as OrderDetails,
  NumbersCancellation,
  IncompleteOrders,
} from '@containers/Order';
import {
  Coverage,
  Pricelists,
  ProductLayout,
  ProductSheet,
  TermsAndConditions,
} from '@containers/Product';
import {
  AddNewProfile,
  Documents,
  EditProfile,
  EndUserProfiles,
  ProfilesLayout,
} from '@containers/Profiles';
import { Cdrs, ReportsLayout, Snapshot } from '@containers/Reports';
import { ResetPassword } from '@containers/ResetPassword';
import {
  CreateTicket,
  OpenTickets,
  TicketDetails,
  TicketsHistory,
  TicketsLayout,
} from '@containers/Tickets';
import {
  UserChangePassword,
  UserContactDetails,
  UserGeneral,
  UserSettingsLayout,
} from '@containers/UserSettings';
import PrivateRoutes from '@features/PrivateRoute';

const routes = (
  <SentryRoutes>
    <Route element={<PrivateRoutes />}>
      <Route path="*" element={<Layout />}>
        <Route path="*" element={<Page404 />} />
      </Route>
      <Route path="/" element={<Layout />}>
        <Route exact element={<Dashboard />} path="/" />
      </Route>
      <Route path="/order" element={<OrderLayout />}>
        <Route path="numbers">
          <Route exact element={<OrderNumbers />} path="" />
          <Route
            exact
            element={<SelectNumbers />}
            path="plans/:id/type/:type/prefixes/:prefixID"
          />
          <Route
            exact
            element={<SelectBackorders />}
            path="backorders/plans/:id/type/:type/prefixes/:prefixID"
          />
          <Route
            exact
            element={<CheckoutNumbersForPlan />}
            path="plans/:id/type/:type/prefixes/:prefixID/checkout"
          />
        </Route>
        <Route
          exact
          element={<OrderDedicatedChannels />}
          path="dedicated-channels"
        />
        <Route
          exact
          element={<CancelDedicatedChannels />}
          path="dedicated-channels/cancel"
        />
        <Route exact element={<OrderSharedChannels />} path="shared-channels" />
        <Route path="pstn-upgrade">
          <Route exact element={<OrderPSTNUpgrade />} path="" />
          <Route
            exact
            element={<CheckoutPSTNUpgrade />}
            path="plans/:id/type/:type/checkout"
          />
        </Route>
        <Route path="incomplete">
          <Route exact element={<IncompleteOrders />} path="" />
          <Route exact element={<OrderDetails />} path=":uuid/details" />
        </Route>
        <Route path="open">
          <Route exact element={<OpenOrders />} path="" />
          <Route exact element={<OrderDetails />} path=":uuid/details" />
        </Route>
        <Route path="history">
          <Route exact element={<OrderHistory />} path="" />
          <Route exact element={<OrderDetails />} path=":uuid/details" />
        </Route>
      </Route>
      <Route path="/numbers" element={<NumbersLayout />}>
        <Route exact element={<MyNumbers />} path="existing" />
        <Route exact element={<Details />} path=":did/details" />
      </Route>
      <Route path="/capacity" element={<CapacityLayout />}>
        <Route path="did-groups">
          <Route exact element={<DidGroupsExisting />} path="existing" />
          <Route exact element={<DidGroupCreate />} path="new" />
          <Route exact element={<DidGroupDetails />} path=":id/edit" />
          <Route exact element={<DidGroupDelete />} path=":id/delete" />
          <Route exact element={<DidGroupRename />} path=":id/rename" />
        </Route>
        <Route
          exact
          element={<DedicatedChannels />}
          path="dedicated-channels"
        />
        <Route path="trunks">
          <Route exact element={<Trunks />} path="" />
          <Route exact element={<TrunkDetails />} path=":trunkID/edit" />
        </Route>
      </Route>
      <Route path="/profiles" element={<ProfilesLayout />}>
        <Route exact element={<EndUserProfiles />} path="existing" />
        <Route exact element={<AddNewProfile />} path="new" />
        <Route path="edit">
          <Route exact element={<EditProfile />} path="" />
          <Route exact element={<EditProfile />} path=":profileID" />
        </Route>
        <Route exact element={<AddNewProfile />} path="edit" />
        <Route exact element={<Documents />} path="documents" />
      </Route>
      <Route path="/product" element={<ProductLayout />}>
        <Route exact element={<Coverage />} path="coverage" />
        <Route exact element={<Pricelists />} path="pricelist" />
        <Route exact element={<ProductSheet />} path="product-sheet" />
        <Route
          exact
          element={<TermsAndConditions />}
          path="terms-and-conditions"
        />
      </Route>
      <Route path="/billing" element={<BillingLayout />}>
        <Route path="invoices">
          <Route exact element={<Invoices />} path="" />
          <Route exact element={<InvoiceDetails />} path=":uuid/details" />
        </Route>
        <Route path="balance">
          <Route exact element={<Balance />} path="" />
          <Route exact element={<BalanceHistory />} path="history" />
        </Route>
      </Route>
      <Route path="/reports" element={<ReportsLayout />}>
        <Route exact element={<Cdrs />} path="cdrs" />
        <Route exact element={<Snapshot />} path="snapshot" />
      </Route>
      <Route path="/tickets" element={<TicketsLayout />}>
        <Route exact element={<OpenTickets />} path="existing" />
        <Route exact element={<CreateTicket />} path="create" />
        <Route exact element={<TicketsHistory />} path="history" />
        <Route exact element={<TicketDetails />} path="details/:ticketID" />
      </Route>
      <Route path="/settings" element={<UserSettingsLayout />}>
        <Route exact element={<UserGeneral />} path="general" />
        <Route exact element={<UserContactDetails />} path="contact" />
        <Route exact element={<UserChangePassword />} path="password/change" />
      </Route>
      <Route path="/api" element={<Layout />}>
        <Route exact element={<ApiPage />} path="settings" />
      </Route>
      <Route path="/cart" element={<Layout />}>
        <Route exact element={<Checkout />} path="checkout" />
        <Route exact element={<NumbersCancellation />} path="cancellation" />
      </Route>
    </Route>
    <Route exact element={<ResetPassword />} path="/password/reset" />
    <Route exact element={<Login />} path="/login" />
    <Route exact element={<Logout />} path="/logout" />
  </SentryRoutes>
);

export default routes;
